import {
  Metric,
  WEIGHT_METRIC_ID,
} from "@/components/metricsComponents/metricsColumns";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useOrg } from "@/lib/states/session/hooks";
import { cn } from "@/lib/utils/ui";
import { trpc } from "@/lib/trpc/client";
import { Check } from "lucide-react";
import React, { useEffect, useState } from "react";

interface MetricTypeProps {
  data: any;
  onDataChanged: (data: any) => void;
}

const MetricType: React.FC<MetricTypeProps> = ({ data, onDataChanged }) => {
  // Data is metric
  const [open, setOpen] = useState(false);
  const org = useOrg();
  const weightUnitLabel = org?.weightUnitDefault === "lbs" ? "lbs" : "kg";
  const { data: metrics } = trpc.metrics.list.useQuery(undefined, {
    enabled: Boolean(org?.id),
  });
  const [currentMetric, setCurrentMetric] = useState<Metric>();
  useEffect(() => {
    if (!currentMetric) {
      setCurrentMetric(metrics?.find((m) => m.id === data));
    }
  }, [metrics, currentMetric]);

  const getMetricUnitLabel = (metric?: Metric) => {
    if (!metric?.unit) {
      return "";
    }

    if (metric.id === WEIGHT_METRIC_ID) {
      return weightUnitLabel;
    }

    return metric.unit;
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {data && (
              <>
                {currentMetric?.name}{" "}
                {getMetricUnitLabel(currentMetric) &&
                  `- ${getMetricUnitLabel(currentMetric)}`}
              </>
            )}
            {!data && "Select metric"}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn("w-[var(--radix-popover-trigger-width)] p-0")}
        >
          <Command>
            <CommandInput placeholder={`Search metrics...`} />
            <CommandEmpty>{`No metrics found`}</CommandEmpty>
            <CommandList className="max-h-[200px] overflow-y-scroll">
              <CommandGroup>
                {(metrics ?? []).map((metric) => {
                  return (
                    <CommandItem
                      key={metric.id}
                      value={metric.id}
                      onSelect={() => {
                        setOpen(false);
                        // onItemSelected?.(item);
                        setCurrentMetric(metric);
                        onDataChanged?.(metric.id);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 opacity-0",
                          metric.id === data && "opacity-100"
                        )}
                      />
                      {metric.name}{" "}
                      {getMetricUnitLabel(metric) &&
                        `- ${getMetricUnitLabel(metric)}`}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MetricType;
